import { useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';

/**
 * Exported so a caller's own CSS transition runs for exactly as long as the swap waits.
 *
 * Short on purpose: the guest is waiting on a list they asked to change, and the swap itself costs
 * a beat on top of this while the new cards lay out. Long enough to read as one motion.
 */
export const FADE_DURATION_MS = 150;

/**
 * How fading content is drawn, with the two halves that have to travel together.
 *
 * Fading content stays mounted, so on opacity alone it would still be hit-testable — and a tap
 * landing there acts on what is leaving, while the handlers on it already belong to what is
 * arriving. Blocking input is what stops that, which is why it is not left to each caller to
 * remember: setting the opacity by hand is how the pairing gets broken.
 */
export function fadeStyle(faded: boolean): CSSProperties {
  return {
    opacity: faded ? 0 : 1,
    transitionDuration: `${FADE_DURATION_MS}ms`,
    pointerEvents: faded ? 'none' : undefined,
  };
}

type TransitionState = 'idle' | 'fading-out' | 'fading-in';

interface UseFadeTransitionOptions<T> {
  items: T[];
  itemsKey?: string;
  onTransitionMidpoint?: () => void;
  onTransitionComplete?: () => void;
}

function sameItems<T>(a: T[], b: T[]): boolean {
  return a.length === b.length && a.every((item, index) => item === b[index]);
}

interface UseFadeTransitionResult<T> {
  displayedItems: T[];
  /**
   * The generation `displayedItems` belongs to, which only advances at the swap. Key a carousel
   * on this rather than on the caller's own key: keyed on the pending one it remounts the moment
   * the guest picks, so the old content animates in and then hard-swaps halfway through.
   */
  displayedKey?: string;
  isFadingOut: boolean;
  isFadingIn: boolean;
}

export function useFadeTransition<T>({
  items,
  itemsKey,
  onTransitionMidpoint,
  onTransitionComplete,
}: UseFadeTransitionOptions<T>): UseFadeTransitionResult<T> {
  const lastKeyRef = useRef<string | undefined>(itemsKey);
  const pendingRef = useRef<{ items: T[]; key: string | undefined } | null>(null);

  const [transitionState, setTransitionState] = useState<TransitionState>('idle');
  const [transitionItems, setTransitionItems] = useState<T[]>(items);
  const [transitionKey, setTransitionKey] = useState<string | undefined>(itemsKey);

  // When no itemsKey, show items directly; otherwise show transition items
  const displayedItems = useMemo(
    () => (itemsKey === undefined ? items : transitionItems),
    [itemsKey, items, transitionItems],
  );

  // Trigger transition or update items when key/items change
  useEffect(() => {
    if (itemsKey === undefined) return;

    if (itemsKey !== lastKeyRef.current && transitionState === 'idle') {
      pendingRef.current = { items, key: itemsKey };
      setTransitionState('fading-out');
    } else if (itemsKey === transitionKey && transitionState === 'idle') {
      // Only when the list really changed. A caller whose array is rebuilt every render would
      // otherwise be re-seeded on every render, and each re-seed renders again — forever.
      setTransitionItems(current => (sameItems(current, items) ? current : items));
    }
  }, [items, itemsKey, transitionKey, transitionState]);

  useEffect(() => {
    if (transitionState !== 'fading-out') return;

    const timer = setTimeout(() => {
      if (pendingRef.current) {
        setTransitionItems(pendingRef.current.items);
        setTransitionKey(pendingRef.current.key);
        lastKeyRef.current = pendingRef.current.key;
        pendingRef.current = null;
      }
      onTransitionMidpoint?.();
      setTransitionState('fading-in');
    }, FADE_DURATION_MS);

    return () => clearTimeout(timer);
  }, [transitionState, onTransitionMidpoint]);

  useEffect(() => {
    if (transitionState !== 'fading-in') return;

    const timer = setTimeout(() => {
      setTransitionState('idle');
      onTransitionComplete?.();
    }, FADE_DURATION_MS);

    return () => clearTimeout(timer);
  }, [transitionState, onTransitionComplete]);

  return {
    displayedItems,
    displayedKey: itemsKey === undefined ? itemsKey : transitionKey,
    isFadingOut: transitionState === 'fading-out',
    isFadingIn: transitionState === 'fading-in',
  };
}
